import { cn } from '@/shadcn-ui/utils';
import { PaymentDetails } from '../types/payment-details';
import { useAdyenCheckout } from '../hooks/use-adyen-checkout';
import { useEffect, useState, useRef } from 'react';
import CheckBadge from '@/icons/check-badge';
import SecuredByAdyenBlurb from './secured-by-adyen-blurb';
import StatusBanner from '@/common/components/status-banner';
import {
  PaymentMethodConfiguration,
  PaymentTitle,
  PaymentType,
  StoredCardPaymentMethod,
} from '../../account/types/payment-method-configuration';
import { RadioGroup, RadioGroupItem } from '@/shadcn-ui/radio-group';
import { capitalizeOnlyFirstLetter } from '@/util-conversion/misc/capitalize-only-first-letter';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AdyenComponent = any;

export type AdyenChangePayload = { data: PaymentDetails; isValid: boolean };

export type PaymentProps = {
  showStoredPaymentDetails?: boolean;
  /** If true, show text about the transaction being secured by Adyen. */
  showSecurityBlurb?: boolean;
  /** If true, show text about free cancellation within 24 hours. */
  showCancellationBlurb?: boolean;
  onPaymentChange: (payload: AdyenChangePayload | null) => void;
  /** Origin key for accessing adyen */
  adyenOriginKey: string;
  /** JS src for adyen JS injection */
  adyenJsSrc: string;
  /** Configures adyen payment component and populates stored payment if it is present */
  paymentMethodConfiguration?: PaymentMethodConfiguration;
  shouldAllowStoredPaymentMethods: boolean;
  paymentType: PaymentType;
  paymentTitle: PaymentTitle;
  storedCardPaymentMethod?: StoredCardPaymentMethod | null;
};

const NEW_PAYMENT_ID = 'newPayment';
const STORED_PAYMENT_ID = 'storedPayment';

export default function Payment({
  showSecurityBlurb = true,
  // Note, we only show the cancellation blurb for orders
  showCancellationBlurb = false,
  onPaymentChange: onChange,
  adyenOriginKey,
  adyenJsSrc,
  paymentMethodConfiguration,
  shouldAllowStoredPaymentMethods,
  paymentType,
  paymentTitle,
  storedCardPaymentMethod,
}: PaymentProps) {
  const [newPaymentContainerRef, setNewPaymentContainerRef] =
    useState<HTMLDivElement | null>(null);
  const [storedPaymentContainerRef, setStoredPaymentContainerRef] =
    useState<HTMLDivElement | null>(null);
  const newPaymentComponent = useRef<AdyenComponent | null>(null);
  const storedPaymentComponent = useRef<AdyenComponent | null>(null);

  const storedCardPayment =
    shouldAllowStoredPaymentMethods && storedCardPaymentMethod
      ? storedCardPaymentMethod
      : null;

  const [selectedPayment, setSelectedPayment] = useState<string>(
    storedCardPayment ? STORED_PAYMENT_ID : NEW_PAYMENT_ID
  );

  // Set the seleted payment to the new payment card component if the stored card payment isn't valid anymore.
  // This helps fix an issue where new card component wasn't rendering after an error with the stored card.
  useEffect(() => {
    if (!storedCardPayment) {
      setSelectedPayment(NEW_PAYMENT_ID);
    }
  }, [storedCardPayment]);

  const { checkout, ready, error } = useAdyenCheckout(
    adyenOriginKey,
    adyenJsSrc,
    paymentMethodConfiguration
  );

  /**
   * Uses Adyen's custom 'card' components for payment UI
   * @see https://docs.adyen.com/online-payments/build-your-integration/sessions-flow/?platform=Web&integration=Components&version=5.66.1
   * @see https://docs.adyen.com/payment-methods/cards/web-component/
   *
   * Uses Adyen's methods like 'unmount' for Adyen components.
   * @see https://docs.adyen.com/online-payments/build-your-integration/sessions-flow/?platform=Web&integration=Drop-in&version=5.64.0#methods
   */
  useEffect(() => {
    if (selectedPayment === STORED_PAYMENT_ID && storedCardPayment) {
      if (!storedPaymentContainerRef || !checkout || storedPaymentComponent.current)
        return;

      /** Unmount the new card payment component if it's already been created */
      if (newPaymentComponent.current) {
        newPaymentComponent.current.unmount();
        newPaymentComponent.current = null;
      }

      /** Create the stored card payment component with stored payment configuration */
      storedPaymentComponent.current = checkout
        .create(PaymentType.card, {
          onChange,
          storedPaymentMethodId: storedCardPayment.id,
          ...storedCardPayment,
        })
        .mount(storedPaymentContainerRef);
    } else {
      if (!newPaymentContainerRef || !checkout || newPaymentComponent.current) return;

      /** Unmount the stored payment component if it's already been created */
      if (storedPaymentComponent.current) {
        storedPaymentComponent.current.unmount();
        storedPaymentComponent.current = null;
      }

      /** Create the new payment component */
      newPaymentComponent.current = checkout
        .create(paymentType, {
          onChange,
        })
        .mount(newPaymentContainerRef);
    }
  }, [
    checkout,
    onChange,
    shouldAllowStoredPaymentMethods,
    selectedPayment,
    storedCardPayment,
    newPaymentContainerRef,
    storedPaymentContainerRef,
    paymentType,
  ]);

  return (
    <div className="fs-exclude">
      {error ? (
        <div className="pb-4">
          <StatusBanner
            title="Error loading payment component. Please try refreshing the page."
            variant="error"
          />
        </div>
      ) : null}

      <RadioGroup
        name="plan"
        defaultValue={selectedPayment}
        onValueChange={setSelectedPayment}
        className="gap-4"
      >
        {storedCardPayment ? (
          <label
            key={STORED_PAYMENT_ID}
            htmlFor={STORED_PAYMENT_ID}
            className="flex flex-col gap-2 rounded-2xl bg-flexcar-neutral85 p-4"
          >
            <div className="flex flex-row justify-between">
              <div className="flex flex-row items-center justify-center gap-3">
                <RadioGroupItem
                  value={STORED_PAYMENT_ID}
                  id={STORED_PAYMENT_ID}
                  className="ring-2"
                  selected={selectedPayment === STORED_PAYMENT_ID}
                />
                <p className="body-strong">
                  {capitalizeOnlyFirstLetter(storedCardPayment.brand)} ••••
                  {storedCardPayment.lastFour}
                </p>
              </div>
            </div>

            <div
              className={cn(`min-h-0`, {
                hidden: selectedPayment !== STORED_PAYMENT_ID,
              })}
            >
              <div ref={setStoredPaymentContainerRef} />
            </div>
          </label>
        ) : null}

        <label
          key={NEW_PAYMENT_ID}
          htmlFor={NEW_PAYMENT_ID}
          className="flex flex-col gap-2 rounded-t-2xl bg-flexcar-neutral85 p-4"
        >
          <div className="flex flex-row justify-between">
            <div className="flex flex-row items-center justify-center gap-3">
              <RadioGroupItem
                value={NEW_PAYMENT_ID}
                id={NEW_PAYMENT_ID}
                className={cn({
                  'hidden': !storedCardPayment,
                  'ring-2': storedCardPayment,
                })}
                selected={selectedPayment === NEW_PAYMENT_ID}
              />
              <p className="body-strong">{paymentTitle}</p>
            </div>
          </div>

          <div
            className={cn(`min-h-0`, {
              hidden: selectedPayment !== NEW_PAYMENT_ID,
            })}
          >
            <div ref={setNewPaymentContainerRef} />
          </div>
        </label>
      </RadioGroup>

      {ready && showSecurityBlurb ? (
        <SecuredByAdyenBlurb
          className={cn('caption-regular bg-flexcar-neutral85 px-4 pb-4 font-medium', {
            'rounded-b-[12px]': !showCancellationBlurb,
          })}
        />
      ) : null}

      {ready && showCancellationBlurb ? (
        <div className="flex items-center justify-center gap-[8px] rounded-b-[12px] bg-flexcar-neutral80 px-[16px] py-[16px]">
          <CheckBadge className="flex-shrink-0" />
          <p className="body">Free cancellation within 24 hours of booking</p>
        </div>
      ) : null}

      {!ready && !error ? (
        <div className="rounded-b-2xl bg-flexcar-neutral85 pt-[16px]" />
      ) : null}

      {ready && !showSecurityBlurb && !showCancellationBlurb ? (
        <div className="rounded-b-2xl bg-flexcar-neutral85 pt-[16px]" />
      ) : null}
    </div>
  );
}
